import i18n, { TFunction } from 'i18next';

// Resolve on access so exported maps follow language changes, including Object.entries callers.
function translatedDisplayMap(namespace: 'main' | 'category' | 'sub', defaults: Record<string, string>): Record<string, string> {
  return Object.defineProperties({}, Object.fromEntries(
    Object.entries(defaults).map(([key, defaultValue]) => [key, {
      enumerable: true,
      get: () => i18n.t(`productCategories.${namespace}.${key}`, { defaultValue }) || defaultValue,
    }])
  ));
}

// Category mapping based on new three-tier category system
// Main categories: FOOD, DRINKS, NON_FOOD

// Main category display names
export const mainCategoryDisplayMap: Record<string, string> = translatedDisplayMap('main', {
  'FOOD': 'Food',
  'DRINKS': 'Drinks',
  'NON_FOOD': 'Non-Food',
});

// FOOD categories display map
export const foodCategoryDisplayMap: Record<string, string> = translatedDisplayMap('category', {
  'MEAT_POULTRY': 'Meat & Poultry',
  'SEAFOOD': 'Seafood',
  'EGGS_AND_DAIRY': 'Eggs & Dairy',
  'FRUITS_AND_VEGETABLES': 'Fruits & Vegetables',
  'OILS_AND_FATS': 'Oils & Fats',
  'GRAINS_AND_STARCHES': 'Grains & Starches',
  'DRY_GOODS': 'Dry Goods',
  'CANNED_AND_PRESERVED_GOODS': 'Canned & Preserved',
  'CONDIMENTS_AND_SAUCES': 'Condiments & Sauces',
  'BAKERY_AND_PASTRY': 'Bakery & Pastry',
  'OTHER': 'Other',
});

// DRINKS categories display map
export const drinksCategoryDisplayMap: Record<string, string> = translatedDisplayMap('category', {
  'WINE': 'Wine',
  'BEER': 'Beer',
  'SPIRITS': 'Spirits',
  'COCKTAILS': 'Cocktails',
  'COLD_DRINKS': 'Cold Drinks',
  'WATER': 'Water',
  'HOT_BEVERAGES': 'Hot Beverages',
  'BAR_INGREDIENTS': 'Bar Ingredients',
  'OTHER': 'Other',
});

// NON_FOOD categories display map
export const nonFoodCategoryDisplayMap: Record<string, string> = translatedDisplayMap('category', {
  'CLEANING_AND_HYGIENE': 'Cleaning & Hygiene',
  'DISPOSABLES_AND_PACKAGING': 'Disposables & Packaging',
  'KITCHEN_CONSUMABLES': 'Kitchen Consumables',
  'BAR_CONSUMABLES': 'Bar Consumables',
  'OFFICE_AND_ADMIN': 'Office & Admin',
  'EMBALLAGE': 'Emballage',
  'OTHER': 'Other',
});

// Combined category display map (for backward compatibility and general use)
export const categoryDisplayMap: Record<string, string> = Object.defineProperties({}, {
  ...Object.getOwnPropertyDescriptors(foodCategoryDisplayMap),
  ...Object.getOwnPropertyDescriptors(drinksCategoryDisplayMap),
  ...Object.getOwnPropertyDescriptors(nonFoodCategoryDisplayMap),
});

// Grouped mid-level category options for pickers (e.g. custom ingredient category).
// 'OTHER' is shared across all three groups, so it's surfaced once at the end instead
// of three identical entries. Group headers reuse the main-category display names.
export function getMidLevelCategoryOptionGroups(language?: string): {
  label: string;
  options: { value: string; label: string }[];
}[] {
  const translate = language ? i18n.getFixedT(language) : i18n.t.bind(i18n);
  return [
    { main: 'FOOD', group: foodCategoryDisplayMap },
    { main: 'DRINKS', group: drinksCategoryDisplayMap },
    { main: 'NON_FOOD', group: nonFoodCategoryDisplayMap },
  ].map(({ main, group }) => ({
    label: translate(`productCategories.main.${main}`, { defaultValue: mainCategoryDisplayMap[main] }) || mainCategoryDisplayMap[main],
    options: Object.keys(group)
      .filter(value => value !== 'OTHER')
      .map(value => ({ value, label: translate(`productCategories.category.${value}`, { defaultValue: group[value] }) || group[value] })),
  })).concat({
    label: translate('productCategories.category.OTHER', { defaultValue: categoryDisplayMap.OTHER }) || categoryDisplayMap.OTHER,
    options: [{ value: 'OTHER', label: translate('productCategories.category.OTHER', { defaultValue: categoryDisplayMap.OTHER }) || categoryDisplayMap.OTHER }],
  });
}

// Subcategory display maps
export const foodSubCategoryDisplayMap: Record<string, string> = translatedDisplayMap('sub', {
  'ASIAN': 'Asian',
  'FROZEN': 'Frozen',
});

export const drinksSubCategoryDisplayMap: Record<string, string> = translatedDisplayMap('sub', {
  'RED_WINE': 'Red Wine',
  'WHITE_WINE': 'White Wine',
  'ROSE_WINE': 'Rosé Wine',
  'BUBBLES': 'Bubbles',
  'COFFEE': 'Coffee',
  'TEA': 'Tea',
  'STILL': 'Still',
  'SPARKLING': 'Sparkling',
  'SOFT_DRINK': 'Soft Drink',
  'JUICE': 'Juice',
});

// Combined subcategory display map
export const subCategoryDisplayMap: Record<string, string> = Object.defineProperties({}, {
  ...Object.getOwnPropertyDescriptors(foodSubCategoryDisplayMap),
  ...Object.getOwnPropertyDescriptors(drinksSubCategoryDisplayMap),
});

// Main category display name
export function getMainCategoryDisplayName(mainCategory?: string): string | null {
  if (!mainCategory) return null;
  return mainCategoryDisplayMap[mainCategory] || mainCategory;
}

// Category display name (uses the appropriate map based on mainCategory if available)
export function getCategoryDisplayName(
  category?: string,
  mainCategory?: string,
  translate: TFunction = i18n.t.bind(i18n),
): string | null {
  if (!category) return null;
  const map = mainCategory === 'FOOD' ? foodCategoryDisplayMap
    : mainCategory === 'DRINKS' ? drinksCategoryDisplayMap
    : mainCategory === 'NON_FOOD' ? nonFoodCategoryDisplayMap : categoryDisplayMap;
  const defaultValue = map[category] || categoryDisplayMap[category] || category.toLowerCase().replace(/_/g, ' ');
  return translate(`productCategories.category.${category}`, { defaultValue }) || defaultValue;
}

// Subcategory display name
export function getSubCategoryDisplayName(subCategory?: string): string | null {
  if (!subCategory) return null;
  return subCategoryDisplayMap[subCategory] || subCategory.toLowerCase().replace(/_/g, ' ');
}

// Category background colors
export function getCategoryBGColor(category: string, isDark: boolean = false): string {
  if (isDark) {
    return `${getCategoryTextColor(category)}26`; // ~15% alpha over the text color
  }
  switch (category) {
    // FOOD categories
    case 'MEAT_POULTRY':
      return '#FEF3F2'; // Light red
    case 'SEAFOOD':
      return '#EFF6FF'; // Very light blue
    case 'EGGS_AND_DAIRY':
      return '#FEFCE8'; // Very light yellow
    case 'FRUITS_AND_VEGETABLES':
      return '#EDFCF2'; // Light green
    case 'OILS_AND_FATS':
      return '#FDF4FF'; // Very light magenta
    case 'GRAINS_AND_STARCHES':
      return '#FFF4E2'; // Light orange
    case 'DRY_GOODS':
      return '#EEF2FF'; // Very light indigo
    case 'CANNED_AND_PRESERVED_GOODS':
      return '#F5F3FF'; // Very light purple
    case 'CONDIMENTS_AND_SAUCES':
      return '#FEFCE8'; // Very light yellow
    case 'BAKERY_AND_PASTRY':
      return '#FFF7ED'; // Very light peach
    case 'OTHER':
      return '#F3F4F6'; // Very light gray
    
    // DRINKS categories
    case 'WINE':
      return '#FAF5FF'; // Very light purple
    case 'BEER':
      return '#FEF3F2'; // Light red
    case 'SPIRITS':
      return '#F5F3FF'; // Very light purple
    case 'COCKTAILS':
      return '#FDF2F8'; // Very light pink
    case 'COLD_DRINKS':
      return '#F0FDFA'; // Very light cyan
    case 'WATER':
      return '#EFF6FF'; // Very light blue
    case 'HOT_BEVERAGES':
      return '#FFF7ED'; // Very light peach
    case 'BAR_INGREDIENTS':
      return '#F7FEE7'; // Very light lime
    
    // NON_FOOD categories
    case 'CLEANING_AND_HYGIENE':
      return '#F3F4F6'; // Very light gray
    case 'DISPOSABLES_AND_PACKAGING':
      return '#F9FAFB'; // Very light gray
    case 'KITCHEN_CONSUMABLES':
      return '#F3F4F6'; // Very light gray
    case 'BAR_CONSUMABLES':
      return '#F3F4F6'; // Very light gray
    case 'OFFICE_AND_ADMIN':
      return '#F3F4F6'; // Very light gray
    case 'EMBALLAGE':
      return '#FEF9C3'; // Very light yellow

    default:
      return '#F3F4F6'; // Very light gray
  }
}

// Category text colors
export function getCategoryTextColor(category: string): string {
  switch (category) {
    // FOOD categories
    case 'MEAT_POULTRY':
      return '#D92D20'; // Red
    case 'SEAFOOD':
      return '#2563EB'; // Blue
    case 'EGGS_AND_DAIRY':
      return '#D97706'; // Amber
    case 'FRUITS_AND_VEGETABLES':
      return '#099250'; // Green
    case 'OILS_AND_FATS':
      return '#DB2777'; // Pink
    case 'GRAINS_AND_STARCHES':
      return '#DB9729'; // Orange
    case 'DRY_GOODS':
      return '#4F46E5'; // Indigo
    case 'CANNED_AND_PRESERVED_GOODS':
      return '#7C3AED'; // Purple
    case 'CONDIMENTS_AND_SAUCES':
      return '#CA8A04'; // Yellow
    case 'BAKERY_AND_PASTRY':
      return '#F59E0B'; // Orange
    case 'OTHER':
      return '#6B7280'; // Gray
    
    // DRINKS categories
    case 'WINE':
      return '#7C3AED'; // Purple
    case 'BEER':
      return '#D92D20'; // Red
    case 'SPIRITS':
      return '#7C3AED'; // Purple
    case 'COCKTAILS':
      return '#EC4899'; // Pink
    case 'COLD_DRINKS':
      return '#0891B2'; // Cyan
    case 'WATER':
      return '#2563EB'; // Blue
    case 'HOT_BEVERAGES':
      return '#C2410C'; // Orange-red
    case 'BAR_INGREDIENTS':
      return '#65A30D'; // Lime
    
    // NON_FOOD categories
    case 'CLEANING_AND_HYGIENE':
      return '#4B5563'; // Dark gray
    case 'DISPOSABLES_AND_PACKAGING':
      return '#4B5563'; // Dark gray
    case 'KITCHEN_CONSUMABLES':
      return '#4B5563'; // Dark gray
    case 'BAR_CONSUMABLES':
      return '#4B5563'; // Dark gray
    case 'OFFICE_AND_ADMIN':
      return '#4B5563'; // Dark gray
    case 'EMBALLAGE':
      return '#A16207'; // Dark yellow/amber

    default:
      return '#6B7280'; // Gray
  }
}

// Category emojis
export function getCategoryEmoji(category: string): string {
  switch (category) {
    // FOOD categories
    case 'MEAT_POULTRY':
      return '🥩';
    case 'SEAFOOD':
      return '🐟';
    case 'EGGS_AND_DAIRY':
      return '🥚';
    case 'FRUITS_AND_VEGETABLES':
      return '🥬';
    case 'OILS_AND_FATS':
      return '🫗';
    case 'GRAINS_AND_STARCHES':
      return '🌾';
    case 'DRY_GOODS':
      return '🥫';
    case 'CANNED_AND_PRESERVED_GOODS':
      return '🥫';
    case 'CONDIMENTS_AND_SAUCES':
      return '🥫';
    case 'BAKERY_AND_PASTRY':
      return '🥖';
    case 'OTHER':
      return '📦';
    
    // DRINKS categories
    case 'WINE':
      return '🍷';
    case 'BEER':
      return '🍺';
    case 'SPIRITS':
      return '🥃';
    case 'COCKTAILS':
      return '🍸';
    case 'COLD_DRINKS':
      return '🥤';
    case 'WATER':
      return '💧';
    case 'HOT_BEVERAGES':
      return '☕';
    case 'BAR_INGREDIENTS':
      return '🍋';
    
    // NON_FOOD categories
    case 'CLEANING_AND_HYGIENE':
      return '🧹';
    case 'DISPOSABLES_AND_PACKAGING':
      return '📦';
    case 'KITCHEN_CONSUMABLES':
      return '🔪';
    case 'BAR_CONSUMABLES':
      return '🍽️';
    case 'OFFICE_AND_ADMIN':
      return '📋';
    case 'EMBALLAGE':
      return '♻️';

    default:
      return '📦';
  }
}

// Subcategory background colors (lighter than categories)
export function getSubCategoryBGColor(subCategory: string, isDark: boolean = false): string {
  if (isDark) {
    return `${getSubCategoryTextColor(subCategory)}26`; // ~15% alpha over the text color
  }
  switch (subCategory) {
    // Food subcategories
    case 'ASIAN':
      return '#F5F3FF'; // Very light purple
    case 'FROZEN':
      return '#E0F2FE'; // Very light blue
    
    // Drinks subcategories
    case 'RED_WINE':
      return '#FEF2F2'; // Very light red
    case 'WHITE_WINE':
      return '#FEFCE8'; // Very light yellow
    case 'ROSE_WINE':
      return '#FDF2F8'; // Very light pink
    case 'BUBBLES':
      return '#F0FDFA'; // Very light cyan
    case 'COFFEE':
      return '#FFF7ED'; // Very light peach
    case 'TEA':
      return '#F7FEE7'; // Very light lime
    case 'STILL':
      return '#EFF6FF'; // Very light blue
    case 'SPARKLING':
      return '#F0FDFA'; // Very light cyan
    case 'SOFT_DRINK':
      return '#F0FDFA'; // Very light cyan
    case 'JUICE':
      return '#FDF2F8'; // Very light pink
    
    default:
      return '#F3F4F6'; // Very light gray
  }
}

// Subcategory text colors
export function getSubCategoryTextColor(subCategory: string): string {
  switch (subCategory) {
    // Food subcategories
    case 'ASIAN':
      return '#7C3AED'; // Purple
    case 'FROZEN':
      return '#0284C7'; // Blue
    
    // Drinks subcategories
    case 'RED_WINE':
      return '#DC2626'; // Red
    case 'WHITE_WINE':
      return '#D97706'; // Amber
    case 'ROSE_WINE':
      return '#EC4899'; // Pink
    case 'BUBBLES':
      return '#0891B2'; // Cyan
    case 'COFFEE':
      return '#C2410C'; // Orange-red
    case 'TEA':
      return '#65A30D'; // Lime
    case 'STILL':
      return '#2563EB'; // Blue
    case 'SPARKLING':
      return '#0891B2'; // Cyan
    case 'SOFT_DRINK':
      return '#0891B2'; // Cyan
    case 'JUICE':
      return '#EC4899'; // Pink
    
    default:
      return '#6B7280'; // Gray
  }
}

// Category styles (for badges)
export function getCategoryStyles(value: string, isDark: boolean = false): { backgroundColor: string; color: string } {
  return {
    backgroundColor: getCategoryBGColor(value, isDark),
    color: getCategoryTextColor(value)
  };
}

// Subcategory styles (for badges)
export function getSubCategoryStyles(value: string, isDark: boolean = false): { backgroundColor: string; color: string } {
  return {
    backgroundColor: getSubCategoryBGColor(value, isDark),
    color: getSubCategoryTextColor(value)
  };
}
